import TdShiftCodeButton from "@components/feature/rosterTable/TdShiftCodeButton";
import TdShiftCodeButtonView from "@components/feature/rosterTable/TdShiftCodeButtonView";
import type * as type from "@vTypes";

import RosterGrid from "@components/feature/roster_create/components/roster-grid.tsx";
import { cn } from "@utils/cn.ts";
import ShiftStatus from "@pages/roster-create/components/shift-status.tsx";
import { memo, useMemo } from "react";
import useRosterConfig from "@/hooks/useRosterConfig";
import useStore from "@/store/store";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { PrevTailData } from "@/hooks/roster-craete/useRosterPrevTail";
import { NURSE_LIST_COLUMNS } from "@components/feature/rosterTable/script";
import type { CellViolationInfo } from "@/services/validation";
import type { InboundInfo } from "@/hooks/roster-craete/useRosterSchedule";
import {
  computeAssignmentStarts,
  computeAssignmentStartsExtended,
  computeInboundAssignmentStarts,
  inboundItemsFromInboundField,
  classifyInboundForGroup,
} from "@/services/assignment";
import type { PrevTailNurseAssignment } from "@/hooks/roster-craete/useRosterPrevTail";
import AssignmentGroupBar from "@/pages/roster-create/components/shift-code/assignment-group-bar";
import type { InboundItem } from "@/hooks/roster-craete/useRosterSchedule";
import {
  resolveShiftFromScheduleCellFromMaps,
  resolveShiftFromScheduleCodeFromMaps,
  type ShiftLookupMaps,
} from "@/utils/shift-util";
import type {
  EmergencyBulkPeriodPick,
  EmergencyPostApplyHighlight,
  EmergencyReplacementSlotPick,
} from "@/types/emergency-replacement.types";
import ShiftCode from "./ThShiftCodeLegend";
import { useRosterFocusStore } from "@/store/rosterFocusStore";
import NurseRowGrid from "@/components/nurse/nurseRowGrid";
import { RosterGridType } from "../roster_create/components/roster-grid-type";
import { useUser } from "@/hooks/useAuth";
import useGradeConfig from "@/hooks/useGradeConfig";
import { resolveGradeLabel } from "@/utils/grade-util";
import { useGroupStore } from "@/store/groupStore";

type NurseRuntimeFields = {
  nurse_id?: string;
  age?: number | string | null;
  grade?: number | null;
  /** 스케줄 API `schedule_ids` — 날짜 인덱스별 시프트 DB id, `availableShifts[].id`와 매칭 */
  schedule_ids?: (number | null)[] | null;
  isAppliedMap?: Record<number, boolean>;
  wantedEntriesMap?: Record<number, string>;
  /** 원티드 엔트리 `shifts_table_id` — `shiftLookupMaps.byNumericId`와 정합 */
  wantedShiftsTableIdsMap?: Record<number, number | null>;
  shiftMessages?: Record<number, string>;
};

function NurseRosterList({
  isEditMode,
  nursesIndex,
  nurse,
  isSubmitted,
  availableShifts,
  shiftLookupMaps,
  modDayShift,
  selectedShift,
  statsColumnShiftIds,
  statsDisplayShifts,
  selectIdForSideprofile,
  prevTailData,
  cellViolationMap,
  isEmergencyPickMode = false,
  onEmergencySlotPick,
  emergencySlotPick = null,
  isEmergencyBulkPickMode = false,
  onEmergencyBulkPick,
  emergencyBulkPeriodPick = null,
  emergencyBulkSelectedSteps = null,
  emergencyReplacementCandidateNurseId = null,
  emergencyPostApplyHighlight = null,
  scheduleYear = 0,
  scheduleMonth = 0,
  schedulePayloadYear,
  schedulePayloadMonth,
  showPerShiftStats = false,
  /** true: 발행 조회 — 읽기 전용 셀, 제출 배지 없음, 전월 tail 없음 */
  issuedRosterView = false,
  visibleShifts,
  roleGroupBottomBorder = false,
  /** 원티드 조정판(모달): 위반 토글을 전역 대신 로컬로 — 미전달이면 일반 근무표 행 */
  violationVisibilityOverride,
  /** false: 셀 클릭으로 열·행 선택(클릭 하이라이트) 안 함 — 호버만 */
  selectionFocusOnClick = true,
  currentGroupId,
}: {
  isEditMode: boolean;
  nursesIndex: number;
  issuedRosterView: boolean;
  nurse: {
    counts: { [key: string]: number | null } | null;
    experience: number;
    id: string;
    name: string;
    age?: number | string | null;
    grade?: number | null;
    preceptor: {
      color: string;
      ee: string[];
      group: { preceptor: string; preceptee: string[] } | number;
      groupIndex?: number;
      or: string[];
      role: string;
    } | null;
    schedule: string[] | null;
    off: number;
    level: string;
    role: string;
    shiftMessages?: Record<number, string>; // 날짜별 메시지 (comment)
    is_inbound?: boolean;
    inbound?: InboundInfo | InboundItem[];
    /** 스케줄 API·발행 병합 행 — `inbound`(배열 또는 `{ inbound_list }`)·기간 바에 사용 */
    assignments?: InboundItem[];
  };
  isSubmitted: boolean;
  availableShifts: type.availableShifts[];
  /** `mergeShiftsForCellLookup` + `buildShiftLookupMaps` — 상위에서 `useShiftLookupMaps`로 생성 */
  shiftLookupMaps: ShiftLookupMaps<type.availableShifts>;
  modDayShift: (
    nurseIndex: number,
    scheduleDay: number,
    changeShift: string,
    cellShiftDbId?: number | null
  ) => void;
  selectedShift: type.Type_availableShifts | null;

  /** 헤더 통계 칩과 동일 열 순서( canonical shift_id ) */
  statsColumnShiftIds?: string[];
  /** 동일 shift_id가 여러 행일 때 DB id로 열·집계 구분 */
  statsDisplayShifts?: type.availableShifts[];
  selectIdForSideprofile?: (bool: boolean, nurseID: string) => void;
  disableManpowerValidation?: boolean; // 최소인원 검증 비활성화 옵션
  violations?: unknown[]; // 위반사항 목록
  copyNurses?: unknown[]; // 전체 간호사 목록 (그룹 합계 계산용)
  prevTailData?: PrevTailData | null; // 전월 말일 근무표 데이터
  cellViolationMap?: Map<string, CellViolationInfo>;
  isEmergencyPickMode?: boolean;
  onEmergencySlotPick?: (pick: EmergencyReplacementSlotPick) => void;
  emergencySlotPick?: EmergencyReplacementSlotPick | null;
  isEmergencyBulkPickMode?: boolean;
  onEmergencyBulkPick?: (pick: {
    nurseId: string;
    nurseName: string;
    dayIndex: number;
    dateISO: string;
    shiftColor?: string;
  }) => void;
  emergencyBulkPeriodPick?: EmergencyBulkPeriodPick | null;
  emergencyBulkSelectedSteps?: Array<{ date: string; nurseId: string }> | null;
  emergencyReplacementCandidateNurseId?: string | null;
  emergencyPostApplyHighlight?: EmergencyPostApplyHighlight | null;
  scheduleYear?: number;
  scheduleMonth?: number;
  /** 스케줄 API 응답의 year/month — 뷰와 일치할 때만 `inbound` 항목으로 기간 바 표시 */
  schedulePayloadYear?: number;
  schedulePayloadMonth?: number;
  /** false면 행 우측 ShiftStatus(근무코드별 집계) 숨김 */
  showPerShiftStats?: boolean;
  /** RN/AN/ETC 등 직군 그룹이 다음 행과 바뀔 때 하단 구분선 */
  roleGroupBottomBorder?: boolean;
  visibleShifts?: string[];
  /** 원티드 조정판 등: 전달 시 위반 표시·원티드 mismatch UX를 조정판 모드로 */
  violationVisibilityOverride?: {
    showConfigViolations: boolean;
    showManpowerViolations: boolean;
  } | null;
  selectionFocusOnClick?: boolean;
  /** 현재 편집 중인 병동 group_id — inbound 잠금 정책에 사용 */
  currentGroupId?: string | null;
}) {
  const nurseRuntime = nurse as unknown as NurseRuntimeFields;
  const isWantedAdjustmentBoard = violationVisibilityOverride != null;
  const nurseId = nurseRuntime.nurse_id || nurse.id;
  const nurseIdAsString = String(nurseId);
  const user = useUser();
  const { selectedGroupId: selectedGroupIdFromStore } = useGroupStore();
  const gradeConfigGroupId = currentGroupId ?? selectedGroupIdFromStore ?? null;
  const { data: gradeData } = useGradeConfig({
    selectedGroupId: gradeConfigGroupId,
  });

  const isMyData = user?.nurse_id === nurseId;

  /**
   * 그룹 기준 잠금 분류는 `inbound`를 우선 사용하고,
   * 일부 응답에서 `inbound`가 비어 있는 행은 `assignments`를 fallback으로 사용한다.
   */
  const inboundForGroupClassification = useMemo(() => {
    const inboundItems = inboundItemsFromInboundField(nurse.inbound);
    if (inboundItems.length > 0) return inboundItems;
    return nurse.assignments ?? [];
  }, [nurse.inbound, nurse.assignments]);

  const { lockedDays, lockedSegments } = useMemo(() => {
    const sy = scheduleYear ?? 0;
    const sm = scheduleMonth ?? 0;
    if (!sy || !sm)
      return { lockedDays: new Set<number>(), lockedSegments: [] };
    const dim = new Date(sy, sm, 0).getDate();
    return classifyInboundForGroup(
      inboundForGroupClassification,
      currentGroupId,
      dim,
      sy,
      sm
    );
  }, [
    inboundForGroupClassification,
    currentGroupId,
    scheduleYear,
    scheduleMonth,
  ]);

  // prev tail 대상 간호사의 assignments 추출 (레거시 nurse_assignments fallback)
  const prevNurseAssignments = useMemo((): PrevTailNurseAssignment[] => {
    if (!prevTailData?.nurses) return [];
    const currentNurseId = String(nurseId);
    const prevNurse = prevTailData.nurses.find(
      (n) => String(n.nurse_id) === currentNurseId
    );
    const row = prevNurse as
      | (typeof prevNurse & {
          assignments?: PrevTailNurseAssignment[];
          nurse_assignments?: PrevTailNurseAssignment[];
        })
      | undefined;
    return row?.assignments ?? row?.nurse_assignments ?? [];
  }, [prevTailData, nurseId]);

  // prev tail 일자 배열 (정렬된 숫자)
  const prevTailDays = useMemo((): number[] => {
    if (!prevTailData) return [];
    const src =
      prevTailData.tail_days?.length > 0
        ? prevTailData.tail_days
        : prevTailData.nurses
          ? (() => {
              const currentNurseId = String(nurseId);
              const prevNurse = prevTailData.nurses.find(
                (n) => String(n.nurse_id) === currentNurseId
              );
              return prevNurse?.shifts
                ? Object.keys(prevNurse.shifts).map(Number)
                : [];
            })()
          : [];
    return [...new Set(src.map(Number).filter((d) => d >= 1 && d <= 31))].sort(
      (a, b) => a - b
    );
  }, [prevTailData, nurseId]);

  // dedup 병합: nurse.assignments(현재월) + prevNurseAssignments(전월)
  const mergedAssignmentItems =
    useMemo((): import("@/hooks/roster-craete/useRosterSchedule").InboundItem[] => {
      const keyOf = (item: {
        reason: string;
        target_group_id?: string;
        startDate?: string;
        start_date?: string;
        endDate?: string | null;
        end_date?: string | null;
      }) =>
        `${item.reason}|${item.target_group_id ?? ""}|${item.startDate ?? item.start_date ?? ""}|${item.endDate ?? item.end_date ?? ""}`;

      const seen = new Set<string>();
      const out: import("@/hooks/roster-craete/useRosterSchedule").InboundItem[] =
        [];

      for (const item of nurse.assignments ?? []) {
        const k = keyOf(item);
        if (seen.has(k)) continue;
        seen.add(k);
        out.push(item);
      }
      for (const item of prevNurseAssignments) {
        const k = keyOf(item);
        if (seen.has(k)) continue;
        seen.add(k);
        const startDate = (item.start_date ??
          (item as any).startDate ??
          "") as string;
        const endDate = (item.end_date ?? (item as any).endDate ?? null) as
          | string
          | null;
        out.push({
          startDate,
          endDate,
          reason: item.reason,
          target_group_id: item.target_group_id,
          target_group_name: item.target_group_name,
        });
      }
      return out;
    }, [nurse.assignments, prevNurseAssignments]);

  // tail 포함 extended 좌표계로 계산 (startDay 음수 = tail, 양수 = 현재월)
  const mergedAssignmentStarts = useMemo(() => {
    const sy = scheduleYear ?? 0;
    const sm = scheduleMonth ?? 0;
    if (!sy || !sm) return [];
    const py = schedulePayloadYear;
    const pm = schedulePayloadMonth;
    const payloadMatchesView =
      py != null &&
      pm != null &&
      Number.isFinite(py) &&
      Number.isFinite(pm) &&
      py === sy &&
      pm === sm;

    if (!payloadMatchesView && prevTailDays.length === 0) return [];

    const items = payloadMatchesView
      ? mergedAssignmentItems
      : prevTailDays.length > 0
        ? mergedAssignmentItems
        : [];
    if (items.length === 0 && prevTailDays.length === 0) return [];

    return computeAssignmentStartsExtended(
      items,
      sy,
      sm,
      prevTailDays.length > 0 ? prevTailDays : undefined
    );
  }, [
    mergedAssignmentItems,
    prevTailDays,
    schedulePayloadYear,
    schedulePayloadMonth,
    scheduleYear,
    scheduleMonth,
  ]);

  // tail 시작점을 가진 dedupKey 집합 (현재월 렌더에서 스킵용)
  const tailStartedDedupKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const s of mergedAssignmentStarts) {
      if (s.startDay < 0 && s.dedupKey) keys.add(s.dedupKey);
    }
    return keys;
  }, [mergedAssignmentStarts]);

  // 전월 tail에서 시작해 현재월로 이어진 구간 메타.
  // 같은(reason/target) 현재월 시작 인디케이터는 tail 기준으로만 보이도록 숨긴다.
  const tailContinuationWindows = useMemo(() => {
    const windows: Array<{
      reason: string;
      targetGroupId?: string;
      startDay: number;
      endDay: number;
    }> = [];

    for (const s of mergedAssignmentStarts) {
      if (s.startDay >= 0) continue;
      // extended rangeLength 규칙: start<0, end>=1 인 경우 range = (-start) + end
      const currentMonthEnd = s.rangeLength + s.startDay;
      if (currentMonthEnd < 1) continue;
      windows.push({
        reason: s.reason,
        targetGroupId: s.targetGroupId,
        startDay: 1,
        endDay: currentMonthEnd,
      });
    }

    return windows;
  }, [mergedAssignmentStarts]);

  const nurseAssignmentStarts = useMemo(() => {
    const sy = scheduleYear ?? 0;
    const sm = scheduleMonth ?? 0;
    const py = schedulePayloadYear;
    const pm = schedulePayloadMonth;
    const payloadMatchesView =
      py != null &&
      pm != null &&
      Number.isFinite(py) &&
      Number.isFinite(pm) &&
      py === sy &&
      pm === sm;

    // lockedSegments 기반: outbound / outsider 잠금 바 포함
    const segmentStarts = lockedSegments;

    const params = {
      scheduleYear: sy,
      scheduleMonth: sm,
      schedulePayloadYear,
      schedulePayloadMonth,
    };

    // currentGroupId가 있으면 lockedSegments가 inbound를 대체
    const inboundStarts = currentGroupId
      ? []
      : computeInboundAssignmentStarts(nurse.inbound, params);

    // 현재월 assignments: tail 시작점인 배정은 mergedAssignmentStarts 에서 처리하므로 스킵
    const assignItems = (nurse.assignments ?? []).filter((item) => {
      if (tailStartedDedupKeys.size === 0) return true;
      const k = `${item.reason}|${item.target_group_id ?? item.targetGroupId ?? ""}|${item.startDate ?? ""}|${item.endDate ?? ""}`;
      return !tailStartedDedupKeys.has(k);
    });
    const fromAssignments =
      payloadMatchesView && assignItems.length > 0
        ? computeAssignmentStarts(assignItems, sy, sm)
        : [];

    const isCoveredByTailContinuation = (s: {
      reason: string;
      startDay: number;
      targetGroupId?: string;
    }) => {
      if (s.startDay < 1) return false;
      return tailContinuationWindows.some((w) => {
        if (w.reason !== s.reason) return false;
        const sameTarget = (w.targetGroupId ?? "") === (s.targetGroupId ?? "");
        if (!sameTarget) return false;
        return s.startDay >= w.startDay && s.startDay <= w.endDay;
      });
    };

    // 세 소스 병합: lockedSegments > fromAssignments > inboundStarts (startDay 키)
    const byDay = new Map<number, (typeof inboundStarts)[number]>();
    for (const s of inboundStarts) {
      if (isCoveredByTailContinuation(s)) continue;
      byDay.set(s.startDay, s);
    }
    for (const s of fromAssignments) {
      if (isCoveredByTailContinuation(s)) continue;
      byDay.set(s.startDay, s);
    }
    // mergedAssignmentStarts 중 현재월 시작 항목 포함 (tail 시작 항목은 위 filter로 이미 제외됨)
    for (const s of mergedAssignmentStarts) {
      if (isCoveredByTailContinuation(s)) continue;
      if (s.startDay >= 1) byDay.set(s.startDay, s);
    }
    for (const s of segmentStarts) {
      if (isCoveredByTailContinuation(s)) continue;
      byDay.set(s.startDay, s);
    }
    return [...byDay.values()].sort((a, b) => a.startDay - b.startDay);
  }, [
    lockedSegments,
    currentGroupId,
    nurse.inbound,
    nurse.assignments,
    schedulePayloadYear,
    schedulePayloadMonth,
    scheduleYear,
    scheduleMonth,
    tailStartedDedupKeys,
    mergedAssignmentStarts,
    tailContinuationWindows,
  ]);
  const assignmentLockedDays = useMemo(() => {
    if (!isWantedAdjustmentBoard) return new Set<number>();
    const sy = scheduleYear ?? 0;
    const sm = scheduleMonth ?? 0;
    const py = schedulePayloadYear;
    const pm = schedulePayloadMonth;
    const payloadMatchesView =
      py != null &&
      pm != null &&
      Number.isFinite(py) &&
      Number.isFinite(pm) &&
      py === sy &&
      pm === sm;
    if (!payloadMatchesView) return new Set<number>();
    const assignItems = mergedAssignmentItems;
    if (assignItems.length === 0) return new Set<number>();
    const starts = computeAssignmentStarts(assignItems, sy, sm);
    const locked = new Set<number>();
    for (const s of starts) {
      const end = s.startDay + s.rangeLength - 1;
      for (let d = s.startDay; d <= end; d++) {
        locked.add(d);
      }
    }
    return locked;
  }, [
    isWantedAdjustmentBoard,
    mergedAssignmentItems,
    schedulePayloadYear,
    schedulePayloadMonth,
    scheduleYear,
    scheduleMonth,
  ]);
  const effectiveLockedDays = useMemo(() => {
    if (assignmentLockedDays.size === 0) return lockedDays;
    const merged = new Set<number>(lockedDays);
    for (const d of assignmentLockedDays) merged.add(d);
    return merged;
  }, [lockedDays, assignmentLockedDays]);

  /**
   * 파견·휴직·inbound 등 배정 바(`AssignmentGroupBar` width)가 당월에 걸친 모든 일자.
   * 원티드 반영사항 보기 딤(opacity-50)은 이 구간·잠금일에 적용하지 않는다.
   */
  const daysCoveredByAssignmentBars = useMemo(() => {
    const sy = scheduleYear ?? 0;
    const sm = scheduleMonth ?? 0;
    if (!sy || !sm) return new Set<number>();
    const dim = new Date(sy, sm, 0).getDate();
    const out = new Set<number>();
    for (const seg of nurseAssignmentStarts) {
      if (seg.startDay < 1) continue;
      const len = seg.rangeLength ?? 1;
      const end = Math.min(seg.startDay + len - 1, dim);
      for (let d = seg.startDay; d <= end; d++) {
        if (d >= 1) out.add(d);
      }
    }
    return out;
  }, [nurseAssignmentStarts, scheduleYear, scheduleMonth]);

  const isRowHovered = useRosterFocusStore(
    (state) => state.hoveredNurseId === nurseIdAsString
  );
  const isRowSelected = useRosterFocusStore(
    (state) => state.selectedNurseId === nurseIdAsString
  );

  const { data: serverConfig } = useRosterConfig();
  const showLevel = serverConfig?.show_level;
  const showPreceptor = serverConfig?.show_preceptor;
  const {
    showWantedViolations,
    showManpowerViolations: storeManpower,
    showConfigViolations: storeConfig,
  } = useStore();

  const showManpowerViolations =
    violationVisibilityOverride?.showManpowerViolations ?? storeManpower;
  const showConfigViolations =
    violationVisibilityOverride?.showConfigViolations ?? storeConfig;

  const preceptorGroupKey =
    nurse.preceptor == null
      ? 0
      : (nurse.preceptor.groupIndex ??
        (typeof nurse.preceptor.group === "number"
          ? nurse.preceptor.group
          : 0));

  function eventPreceptorEnter(num: number) {
    document
      .querySelectorAll(`.preceptorGroup${num}`)
      .forEach((tr) => tr.classList.add("hover"));
  }
  function eventPreceptorLeave(num: number) {
    document
      .querySelectorAll(`.preceptorGroup${num}`)
      .forEach((tr) => tr.classList.remove("hover"));
  }
  function eventPreceptorClick(num: number) {
    document
      .querySelectorAll(`.preceptorGroup:not(.preceptorGroup${num}).click`)
      .forEach((tr) => tr.classList.remove("click"));
    document
      .querySelectorAll(`.preceptorGroup${num}`)
      .forEach((tr) => tr.classList.toggle("click"));
  }

  return (
    <NurseRowGrid
      nurseId={nurseIdAsString}
      isRowSelected={isRowSelected}
      isRowHovered={isRowHovered}
      roleGroupBottomBorder={roleGroupBottomBorder}
    >
      <div className="flex gap-[10px] font-pretendard">
        <RosterGrid
          className={cn(
            nurse.preceptor && "preceptorGroup",
            nurse.preceptor && `preceptorGroup${preceptorGroupKey}`
          )}
          hasPrevTail={issuedRosterView ? false : !!prevTailData}
          gridType={RosterGridType.BODY}
        >
          <div
            className={cn(
              "flex w-full min-w-0 flex-1 flex-shrink-0 items-center justify-start gap-0 "
            )}
          >
            <div
              className="rt_nurse_moveHandler flex-shrink-0 flex justify-center"
              style={{ width: NURSE_LIST_COLUMNS.handle }}
            >
              <span
                className={cn(
                  "text-[12px] text-[#666666] text-center",
                  isMyData && "text-sig font-semibold"
                )}
              >
                {nursesIndex + 1}
              </span>
            </div>

            <div
              className=" flex-shrink-0 flex items-center justify-center gap-1"
              style={{ width: NURSE_LIST_COLUMNS.name }}
            >
              <div
                className={cn(
                  " flex items-center gap-1",
                  issuedRosterView && "justify-center"
                )}
              >
                {!issuedRosterView && (
                  <div
                    className={`submitMark ${isSubmitted && "submitted"} hoverMessageWrap`}
                  >
                    <div className="hoverMessage">
                      {isSubmitted ? "제출완료" : "미제출"}
                    </div>
                  </div>
                )}

                <div
                  className={cn(
                    "rt_nurse_name_block_name",
                    !issuedRosterView &&
                      "cursor-pointer hover:opacity-70 transition-opacity",
                    isMyData && "text-sig! font-semibold"
                  )}
                  onClick={() => {
                    if (
                      !issuedRosterView &&
                      selectIdForSideprofile &&
                      nurseId
                    ) {
                      selectIdForSideprofile(true, nurseId);
                    }
                  }}
                >
                  {nurse.name}
                </div>
              </div>
            </div>

            {/* 프리셉터 */}
            {showPreceptor && (
              <div
                className="flex-shrink-0 flex justify-center items-center"
                style={{ width: NURSE_LIST_COLUMNS.preceptor }}
              >
                <div className=" rt_nurse_name_gridWrap flex justify-center items-center">
                  {nurse.preceptor ? (
                    <button
                      type="button"
                      className={`preceptorMark hoverMessageWrap size-2.5! ${
                        nurse.preceptor.role !== "preceptor" ? "border-3" : ""
                      }`}
                      style={{
                        backgroundColor: `rgb(${nurse.preceptor.color})`,
                        ...(nurse.preceptor.role !== "preceptor"
                          ? {
                              borderColor: `rgb(${nurse.preceptor.color})`,
                              backgroundColor: "transparent",
                            }
                          : {}),
                      }}
                      onMouseEnter={() => {
                        if (nurse.preceptor) {
                          eventPreceptorEnter(preceptorGroupKey);
                        }
                      }}
                      onMouseLeave={() => {
                        if (nurse.preceptor) {
                          eventPreceptorLeave(preceptorGroupKey);
                        }
                      }}
                      onClick={() => {
                        if (nurse.preceptor) {
                          eventPreceptorClick(preceptorGroupKey);
                        }
                      }}
                    >
                      <div className="hoverMessage">
                        {nurse.preceptor.role === "preceptor"
                          ? `프리셉티 ${nurse.preceptor.ee.map((text) => text)}`
                          : `프리셉터 ${nurse.preceptor.or.map((text) => text)}`}
                      </div>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={`preceptorMark hoverMessageWrap bg-transparent! size-2.5! cursor-auto!`}
                    />
                  )}
                </div>
              </div>
            )}

            {/* 나이 */}
            {/* <div
              className="rt_nurse_role flex-shrink-0 text-center"
              style={{ color: "#939399", width: NURSE_LIST_COLUMNS.age }}
            >
              <div className="">
                <div className={cn(isMyData && "text-sig! font-semibold")}>
                  {nurse?.age ?? "-"}
                </div>
              </div>
            </div> */}

            {/* Grade */}
            <div
              className="rt_nurse_role flex-shrink-0 text-center"
              style={{ color: "#939399", width: NURSE_LIST_COLUMNS.grade }}
            >
              <div className="">
                <div
                  className={cn(
                    "flex items-center justify-center",
                    isMyData && "text-sig! font-semibold "
                  )}
                >
                  <span className="truncate max-w-[72px]! overflow-hidden! inline-block">
                    {nurse.grade && nurse.grade !== 0
                      ? resolveGradeLabel(
                          nurse.grade,
                          gradeData?.gradeSelectOptions,
                          gradeData?.grade_names ?? undefined
                        )
                      : "-"}
                  </span>
                </div>
              </div>
            </div>

            {/* 직군 */}
            {showLevel && (
              <div
                className="rt_nurse_role flex-shrink-0 text-center"
                style={{ color: "#939399", width: NURSE_LIST_COLUMNS.role }}
              >
                <div className="">
                  <div className={cn(isMyData && "text-sig! font-semibold")}>
                    <div className="whitespace-nowrap overflow-hidden text-ellipsis">
                      {nurse.role}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 경력 */}
            <div
              className="rt_nurse_role flex-shrink-0 text-center"
              style={{ color: "#939399", width: NURSE_LIST_COLUMNS.experience }}
            >
              <div className={cn(isMyData && "text-sig! font-semibold")}>
                <div className="">{nurse.experience}년</div>
              </div>
            </div>
          </div>
          {/* Schedule*/}
          <div
            className={cn(
              "flex relative",
              "after:absolute after:inset-y-0 after:border-r after:border-dotted after:border-[#D6D6DE] after:w-0 after:content-[''] after:-right-[3px]"
            )}
            data-roster-schedule-row=""
          >
            {/* 전월 말일 데이터 표시 */}
            {!issuedRosterView &&
              prevTailData?.nurses &&
              (() => {
                // nurseId와 동일 기준: nurse_id가 ""이면 id를 쓰는데, ??만 쓰면 ""에서 id로 못 넘어가 매칭 실패함
                const currentNurseId = String(nurseId);
                const prevNurse = prevTailData.nurses.find(
                  (n) => String(n.nurse_id) === currentNurseId
                );

                const prevDaysSource =
                  prevTailData.tail_days?.length > 0
                    ? prevTailData.tail_days
                    : prevNurse?.shifts
                      ? Object.keys(prevNurse.shifts).map(Number)
                      : [];
                const prevDays = prevDaysSource
                  .map(Number)
                  .filter((day) => day >= 1 && day <= 31)
                  .sort((a, b) => a - b);

                if (prevDays.length === 0) return null;

                return (
                  <div className="flex items-center  relative ">
                    <div className="absolute inset-0 -top-[8px]  left-[1px] border-x border-dotted"></div>
                    {prevDays.map((day, i) => {
                      const shiftCode = prevNurse?.shifts?.[String(day)];
                      const shiftMeta = resolveShiftFromScheduleCodeFromMaps(
                        shiftCode,
                        shiftLookupMaps
                      );

                      // 음수 인덱스로 cellViolationMap lookup (설정 위반만 해당)
                      const tailIndex = i - prevDays.length; // -3, -2, -1
                      const cellKey = `${nurseId}-${tailIndex}`;
                      const cellInfo = cellViolationMap?.get(cellKey);
                      const isError = cellInfo?.isError ?? false;
                      const violationMessages = cellInfo?.messages ?? [];
                      const violationGroups = cellInfo?.groups ?? [];

                      const isManpowerMessage = (msg: string) =>
                        msg.includes("인원 미달") || msg.includes("인원 초과");
                      const isManpowerType = (type: string) =>
                        type === "manpower_shortage" ||
                        type === "manpower_excess";
                      const manpowerViolationMessages =
                        violationMessages.filter((msg) =>
                          isManpowerMessage(msg)
                        ) || [];
                      const configViolationMessages =
                        violationMessages.filter(
                          (msg) => !isManpowerMessage(msg)
                        ) || [];

                      const displayedViolationMessages = [
                        ...(showManpowerViolations && isError
                          ? manpowerViolationMessages
                          : []),
                        ...(showConfigViolations
                          ? configViolationMessages
                          : []),
                      ];

                      const visibleViolationGroups =
                        violationGroups.filter((vg) => {
                          return (
                            (isManpowerType(vg.type) &&
                              showManpowerViolations) ||
                            (!isManpowerType(vg.type) &&
                              showConfigViolations)
                          );
                        }) ?? [];

                      return (
                        <div key={`prev-${day}`} data-column={`prev-day${day}`}>
                          <div
                            className="p-[3px] flex items-center relative"
                            data-roster-shift-cell=""
                          >
                            {/* 배정 구간 바 (파견·휴직 등) — tail 시작 셀 기준으로 현재월까지 가로지름 */}
                            {(() => {
                              const asgn = mergedAssignmentStarts.find(
                                (s) => s.startDay === tailIndex
                              );
                              if (!asgn) return null;
                              return (
                                <AssignmentGroupBar
                                  key={`tail-asgn-${nursesIndex}-${tailIndex}-${asgn.reason}`}
                                  group={asgn}
                                />
                              );
                            })()}

                            {/* 전월 tail 영역에서도 그룹 바 표시 (교차월 연속위반 시각 정합성) */}
                            {visibleViolationGroups.length > 0 &&
                              visibleViolationGroups.map((vg, idx) => {
                                // 시작 셀에서만 바 렌더링
                                if (!vg.isStart) return null;

                                const zIndex = 10 + idx;
                                const stableKey = `${nursesIndex}-${tailIndex}-${vg.type}-${vg.startDay}-${vg.endDay}`;
                                return (
                                  <div
                                    key={stableKey}
                                    className={cn(
                                      "violation-group-bar",
                                      "absolute top-1/2 pointer-events-none -translate-y-1/2 left-[1px] border-3 rounded-sm bg-red-400/30 border-red-500",
                                      "animate-in fade-in duration-300",
                                      (vg.rangeLength ?? 1) === 1
                                        ? "size-7.5"
                                        : "h-7.5"
                                    )}
                                    data-violation-type={vg.type}
                                    style={{
                                      zIndex,
                                      willChange: "opacity, transform",
                                      ...((vg.rangeLength ?? 1) > 1
                                        ? {
                                            width: `calc(${vg.rangeLength ?? 1} * 100% - 4px)`,
                                          }
                                        : {}),
                                    }}
                                  >
                                    <div
                                      className={cn(
                                        "absolute -right-[6px] rounded-full -top-[6px] size-2 bg-red-500"
                                      )}
                                    />
                                  </div>
                                );
                              })}

                            {displayedViolationMessages.length > 0 ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div
                                    className={cn(
                                      "opacity-70",
                                      isEditMode
                                        ? "cursor-not-allowed"
                                        : "cursor-default"
                                    )}
                                    data-nurse-id={nurseId}
                                    data-day={tailIndex + 1}
                                    data-is-error={isError ? "true" : "false"}
                                    data-default-shift={
                                      shiftMeta?.default_shift ?? ""
                                    }
                                    data-shift-gb={shiftMeta?.shift_gb ?? ""}
                                    style={{
                                      backgroundColor:
                                        shiftMeta?.color ?? "#d6d6de",
                                    }}
                                  >
                                    <ShiftCode
                                      code={
                                        shiftMeta?.shift_id ?? shiftCode ?? "-"
                                      }
                                      color={shiftMeta?.color ?? "#d6d6de"}
                                    />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="bg-[#444444] text-white whitespace-nowrap">
                                  <div className="space-y-1">
                                    <p className="font-semibold text-sm mb-1">
                                      위반사항:
                                    </p>
                                    {displayedViolationMessages.map(
                                      (msg, idx) => (
                                        <p key={idx} className="text-xs">
                                          {msg}
                                        </p>
                                      )
                                    )}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <div
                                className={cn(
                                  " opacity-70",
                                  isEditMode
                                    ? "cursor-not-allowed"
                                    : "cursor-default"
                                )}
                                data-nurse-id={nurseId}
                                data-day={tailIndex + 1}
                                data-is-error={isError ? "true" : "false"}
                                data-default-shift={
                                  shiftMeta?.default_shift ?? ""
                                }
                                data-shift-gb={shiftMeta?.shift_gb ?? ""}
                              >
                                <ShiftCode
                                  code={shiftMeta?.shift_id ?? shiftCode ?? "-"}
                                  color={shiftMeta?.color ?? "#d6d6de"}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

            <div className="flex items-center   ">
              {/* 현재 월 스케줄 */}
              {issuedRosterView
                ? nurse.schedule?.map((code, index) => {
                    const numericId = nurseRuntime.schedule_ids?.[index];
                    const resolved = resolveShiftFromScheduleCellFromMaps(
                      code,
                      numericId ?? null,
                      shiftLookupMaps
                    );
                    const shift = resolved
                      ? (availableShifts.find(
                          (item) => item.id === resolved.id
                        ) ?? resolved)
                      : undefined;
                    const shiftForView = shift
                      ? {
                          ...shift,
                          default_shift: shift.default_shift ?? undefined,
                          shift_gb: shift.shift_gb ?? undefined,
                        }
                      : undefined;
                    const assignmentStart =
                      nurseAssignmentStarts.find(
                        (s) => s.startDay === index + 1
                      ) ?? null;
                    return (
                      <TdShiftCodeButtonView
                        key={index}
                        day={index + 1}
                        shift={shiftForView}
                        nurseId={nurseIdAsString}
                        nursesIndex={nursesIndex}
                        assignmentStart={assignmentStart}
                      />
                    );
                  })
                : nurse.schedule?.map((code, index) => {
                    const numericId = nurseRuntime.schedule_ids?.[index];

                    const shift = resolveShiftFromScheduleCellFromMaps(
                      code,
                      numericId ?? null,
                      shiftLookupMaps
                    );

                    const shiftForTd = shift
                      ? {
                          ...shift,
                          shift_gb: shift.shift_gb ?? undefined,
                        }
                      : undefined;
                    const cellKey = `${nurseId}-${index}`;
                    const cellInfo = cellViolationMap?.get(cellKey);
                    const isError = cellInfo?.isError ?? false;
                    const violationGroups = cellInfo?.groups ?? [];
                    const violationMessagesForTooltip =
                      cellInfo?.messages ?? [];

                    const assignmentStart =
                      nurseAssignmentStarts.find(
                        (s) => s.startDay === index + 1
                      ) ?? null;

                    const dayNumber = index + 1;
                    const comment = nurseRuntime.shiftMessages?.[dayNumber];
                    const isApplied =
                      nurseRuntime.isAppliedMap?.[dayNumber] ?? undefined;
                    const wantedShiftId =
                      nurseRuntime.wantedEntriesMap?.[dayNumber];
                    const wantedTableId =
                      nurseRuntime.wantedShiftsTableIdsMap?.[dayNumber];
                    const currentShiftId = shift?.shift_id ?? code;

                    const isWantedShift = showWantedViolations && wantedShiftId;
                    const skipWantedViewOpacityForInbound =
                      effectiveLockedDays.has(dayNumber) ||
                      daysCoveredByAssignmentBars.has(dayNumber);
                    const shouldApplyOpacity =
                      !isWantedAdjustmentBoard &&
                      showWantedViolations &&
                      !isWantedShift &&
                      !skipWantedViewOpacityForInbound;

                    let isWantedMismatch = false;
                    if (
                      wantedShiftId &&
                      (isWantedAdjustmentBoard || showWantedViolations)
                    ) {
                      const resolvedWanted =
                        resolveShiftFromScheduleCellFromMaps(
                          wantedShiftId,
                          wantedTableId ?? null,
                          shiftLookupMaps
                        );
                      const resolvedCurrent =
                        shift ??
                        resolveShiftFromScheduleCodeFromMaps(
                          currentShiftId,
                          shiftLookupMaps
                        );
                      isWantedMismatch =
                        resolvedWanted?.shift_id !== resolvedCurrent?.shift_id;
                    }
                    if (skipWantedViewOpacityForInbound) {
                      isWantedMismatch = false;
                    }

                    return (
                      <div
                        key={index}
                        className={cn(
                          shouldApplyOpacity ? "opacity-50" : "opacity-100 ",
                          "transition-opacity duration-300"
                        )}
                      >
                        <TdShiftCodeButton
                          isEveryFifth={!((index + 1) % 5)}
                          nurse_id={nurseId}
                          nursesIndex={nursesIndex}
                          isFirstNurseRow={nursesIndex === 0}
                          day={index + 1}
                          shift={shiftForTd}
                          modDayShift={modDayShift}
                          selectedShift={selectedShift}
                          isError={isError}
                          violationGroup={
                            violationGroups.length > 0
                              ? violationGroups[0]
                              : null
                          }
                          violationGroups={
                            violationGroups.length > 0 ? violationGroups : null
                          }
                          assignmentStart={assignmentStart}
                          violationMessages={violationMessagesForTooltip}
                          comment={comment}
                          isApplied={isApplied}
                          isWantedMismatch={isWantedMismatch}
                          wantedShiftId={wantedShiftId}
                          cellScheduleCode={code}
                          isEmergencyPickMode={isEmergencyPickMode}
                          onEmergencySlotPick={onEmergencySlotPick}
                          emergencySlotPick={emergencySlotPick}
                          isEmergencyBulkPickMode={isEmergencyBulkPickMode}
                          onEmergencyBulkPick={onEmergencyBulkPick}
                          emergencyBulkPeriodPick={emergencyBulkPeriodPick}
                          emergencyBulkSelectedSteps={
                            emergencyBulkSelectedSteps
                          }
                          emergencyReplacementCandidateNurseId={
                            emergencyReplacementCandidateNurseId
                          }
                          emergencyPostApplyHighlight={
                            emergencyPostApplyHighlight
                          }
                          nurseName={nurse.name}
                          scheduleYear={scheduleYear}
                          scheduleMonth={scheduleMonth}
                          violationVisibilityOverride={
                            isWantedAdjustmentBoard
                              ? {
                                  showConfigViolations,
                                  showManpowerViolations,
                                }
                              : undefined
                          }
                          isEditModeOverride={
                            isWantedAdjustmentBoard ? isEditMode : undefined
                          }
                          selectionFocusOnClick={selectionFocusOnClick}
                          isAssignmentLocked={effectiveLockedDays.has(
                            index + 1
                          )}
                        />
                      </div>
                    );
                  })}
            </div>
          </div>
        </RosterGrid>
        {/* 헤더 통계 토글 열과 동일 폭 (Roster_create: 우측 size-10) */}
        <div className="size-10 shrink-0 rounded-full" aria-hidden />
      </div>
      {/* DENO - Status 분리함.. */}
      {showPerShiftStats && (
        <div className=" animate-in fade-in duration-200 mt-[4px] pr-2">
          <ShiftStatus
            nurseSchedule={
              effectiveLockedDays.size > 0
                ? ((nurse.schedule ?? []).map((code, i) =>
                    effectiveLockedDays.has(i + 1) ? null : code
                  ) as string[])
                : (nurse.schedule ?? [])
            }
            scheduleIds={nurseRuntime.schedule_ids ?? undefined}
            shiftDefinitions={availableShifts}
            visibleShifts={visibleShifts}
            columnShiftIds={statsColumnShiftIds}
            columnShifts={statsDisplayShifts}
            counts={nurse.counts}
          />
        </div>
      )}
    </NurseRowGrid>
  );
}
// React.memo로 메모이제이션하여 props가 변경되지 않으면 재렌더링 방지
export default memo(NurseRosterList);
